import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as paymentsApi from '../../api/payments';
import * as invoicesApi from '../../api/invoices';
import { formatDate, formatCurrency } from '../../utils/format';

export default function Payments() {
  const { actor } = useAuth();
  const canVerify = actor.role === 'super_admin' || actor.role === 'admin';
  const isClient = actor.role === 'client';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [form, setForm] = useState({ invoiceId: '', amount: '', paymentDate: '', file: null });

  const load = useCallback(() => {
    if (!canVerify) {
      setLoading(false);
      return;
    }
    setLoading(true);
    paymentsApi
      .listPayments({})
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load payments.'))
      .finally(() => setLoading(false));
  }, [canVerify]);

  useEffect(() => {
    load();
  }, [load]);

  const openRecord = async () => {
    setForm({ invoiceId: '', amount: '', paymentDate: '', file: null });
    const res = await invoicesApi.listInvoices({});
    setInvoiceOptions(res.data.filter((i) => i.status !== 'Paid').map((i) => ({ value: i.id, label: `${i.invoiceNumber} - ${formatCurrency(i.totalAmount)}` })));
    setShowForm(true);
  };

  const handleRecord = async (e) => {
    e.preventDefault();
    try {
      await paymentsApi.recordPayment(form);
      setSuccess('Payment recorded. It will be verified by the firm shortly.');
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record payment.');
    }
  };

  const handleVerify = async (row, decision) => {
    try {
      await paymentsApi.verifyPayment(row.id, decision);
      setSuccess(`Payment ${decision.toLowerCase()}.`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payment.');
    }
  };

  const columns = [
    { key: 'invoice', label: 'Invoice', render: (r) => r.invoice?.invoiceNumber || '-' },
    { key: 'client', label: 'Client', render: (r) => r.invoice?.client?.clientName || '-' },
    { key: 'amount', label: 'Amount', render: (r) => formatCurrency(r.amount) },
    { key: 'paymentDate', label: 'Payment Date', render: (r) => formatDate(r.paymentDate) },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => r.status === 'Pending' && canVerify && (
        <div className="btn-group">
          <button type="button" className="btn-link" onClick={() => handleVerify(r, 'Verified')}>Verify</button>
          <button type="button" className="btn-link" onClick={() => handleVerify(r, 'Rejected')}>Reject</button>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Payments">
      <div className="page-header">
        <div>
          <h1 className="heading">{canVerify ? 'Payment Verification' : 'My Payments'}</h1>
          <p className="page-subtext">{canVerify ? 'Review and verify payments submitted by clients.' : 'Submit proof of payment against an invoice.'}</p>
        </div>
        {isClient && <button type="button" className="btn" onClick={openRecord}>Record a Payment</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      {canVerify && (loading ? <Loader text="Loading payments..." /> : <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No payments recorded yet." />)}
      {!canVerify && !isClient && <p className="text-muted">Payments are managed by the firm.</p>}
      {isClient && <p className="text-muted">Use "Record a Payment" after you have paid an invoice, so the firm can verify it.</p>}

      {showForm && (
        <Modal
          title="Record a Payment"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="payment-form" className="btn">Submit</button>
            </>
          }
        >
          <form id="payment-form" onSubmit={handleRecord}>
            <div className="form-grid single-column">
              <FormField label="Invoice" type="select" options={invoiceOptions} value={form.invoiceId} onChange={(e) => setForm({ ...form, invoiceId: e.target.value })} required />
              <FormField label="Amount Paid" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
              <FormField label="Payment Date" type="date" value={form.paymentDate} onChange={(e) => setForm({ ...form, paymentDate: e.target.value })} />
              <FormField label="Proof of Payment (optional)" type="file" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} />
            </div>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
